import { z } from 'zod';
import { FILL_STATES } from '../constants/fillState.constant';
import { ORG_UNIT_TYPES } from '../constants/identity.constant';
import { OFFICER_DUTY_STATES, officerDutyStateOf, type OfficerDutyState } from '../constants/officerDutyState.constant';
import { RAISER_LEVELS } from '../constants/raiserLevel.constant';
import {
  OPS_LIST_DEFAULT_LIMIT,
  OPS_LIST_MAX_LIMIT,
  OPS_SUMMARY_TILE_IDS,
  OPS_SUMMARY_UNITS,
} from '../constants/officerOps.constant';
import {
  ARRANGEMENT_LIMITS,
  ARRANGEMENT_STATUSES,
  calendarDateSchema,
  type ArrangementStatus,
} from './arrangement.schema';
import { basePaginationSchema, objectIdSchema } from './common.schema';
import { orgUnitSummarySchema } from './orgUnit.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE OPERATIONS SURFACE'S CONTRACT — `/app/operations/*`. [P6d]
 *
 *  ⚠️ IT IS READ-ONLY, AND THAT IS STRUCTURAL RATHER THAN A PROMISE. There is no input schema
 *  in this file that is not a query string: no body, no params beyond an id, and no route
 *  below it that is anything but a `GET`. A nodal who wants to CHANGE something opens the
 *  dashboard.
 *
 *  ⚠️ AND IT IS A DIFFERENT AUDIENCE FROM `officerApp.schema.ts`, ON THE SAME MOUNT.
 *  `/app/arrangements` answers "what am I on" and is confined by the caller's ASSIGNMENT;
 *  these answer "what is happening in my subtree" and are confined by FR-3.6's
 *  `visible_org_unit_ids`. Two questions, two isolation models, one surface — which is why the
 *  routes are separately named rather than a mode on the existing ones.
 *
 *  ⚠️ NOTHING HERE COUNTS COMMAND. `namedCount` and `allocatedCount` are FORCE totals
 *  (FR-5.2.6b, closing OD-9), taken verbatim from the list the dashboard already renders, and
 *  never share a line with a command field.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

// ── The list — GET /app/operations/arrangements ──────────────────────────────────

/**
 * ⚠️ THERE IS NO `sortBy`, AND THE ABSENCE IS THE DECISION §2a ASKED FOR. "Sort: date. Keep it
 * to that — this is a phone, not the dashboard." So the column is fixed to `paDate` in the
 * service and only the DIRECTION is a parameter. A `sortBy` allowlist would need an index per
 * value (§7) and would put four controls on a 360px screen to answer a question nobody on the
 * ground is asking.
 *
 * ⚠️ `dateFrom`/`dateTo` ARE OPERATIONAL, AND `sortBy` IS ADMINISTRATIVE. CLAUDE.md [amended
 * D4]: `pa_date` is "the order was dated X" and `first_duty_date`/`last_duty_date` are "there
 * is duty on X". A nodal filtering by date means the second — they are asking what is running
 * this week — and `listArrangements` already implements exactly that as a two-column overlap.
 * The default sort stays administrative because that is what groups an order's members
 * together.
 */
export const listOpsArrangementsQuerySchema = basePaginationSchema.omit({ limit: true }).extend({
  limit: z.coerce.number().int().min(1).max(OPS_LIST_MAX_LIMIT).default(OPS_LIST_DEFAULT_LIMIT),
  /**
   * [amended C5c §7] The PA number, the reference, the title or a place name — whatever
   * `listArrangements` searches, through its one `arrangementSearchPredicate`. A substring, in any case.
   */
  /** [PLACE-500] The same predicate reads duty places, so the term may be as long as one. */
  search: z.string().trim().min(1).max(ARRANGEMENT_LIMITS.PLACE_NAME).optional(),
  dateFrom: calendarDateSchema.optional(),
  dateTo: calendarDateSchema.optional(),
  /**
   * ⚠️ THE OFFICER-FACING STATE, NEVER THE RAW STATUS — AND THAT IS AN EXISTING RULE RATHER
   * THAN A P6d PREFERENCE.
   *
   * `officerDutyState.constant.ts`: "NO CLIENT MAY RENDER `status` DIRECTLY", because naming
   * the first officer moves a published order to `ALLOCATION_IN_PROGRESS` and printing that to
   * a person describes a nodal's workflow rather than the state of the order. A `<select>`
   * whose options were the raw enum would be exactly that, on the smallest screen in the
   * product.
   *
   * The reduction is MANY-TO-ONE, so the service expands this into a status SET — see
   * `statusesOfOfficerDutyState` below, which derives the inverse rather than transcribing it.
   *
   * ⚠️ [P7A §5] `CANCELLED` IS STILL ACCEPTED, AND DUTY MITRA NO LONGER OFFERS IT. The list is
   * `show: 'current'` and every cancelled order is archived, so it has never matched here — the
   * select offers `OPS_STATE_FILTERS` instead. Narrowing THIS enum would turn a native client's
   * true-but-empty answer into a 422, so the server keeps the whole tuple.
   */
  state: z.enum(OFFICER_DUTY_STATES).optional(),
  divisionOrgUnitId: objectIdSchema.optional(),
  /**
   * ⚠️ [P7A §5] THE RAISER'S LEVEL — `HQ` · `DIVISION` · `UNIT`, the row's own `raisedAtLevel`.
   *
   * OPTIONAL, and absent means "any level". `null` is not a value here: an order whose raiser does
   * not resolve carries no level and is found under none of the three, only in the unfiltered list.
   *
   * ⚠️ IT IS NOT A KEY OF THE DASHBOARD'S `listArrangementsQuerySchema`, AND THAT IS THE SEARCH HINT.
   * `listArrangementsWithHint` builds its probes from the page's own query through
   * `arrangementListFilter`; a raiser key there would have to be resolved inside every probe too, or
   * a hint would answer for a request the page never made. This route has no hint, so the level is
   * resolved to unit ids by `listArrangements` and handed to the builder as an argument that is not
   * a wire key — see `orgUnitIdsAtRaiserLevel`.
   */
  raisedAtLevel: z.enum(RAISER_LEVELS).optional(),
});

/**
 * ⚠️ THE INVERSE OF `officerDutyStateOf`, DERIVED FROM IT — NEVER A SECOND TABLE.
 *
 * A hand-written `Record<OfficerDutyState, ArrangementStatus[]>` would be a second expression
 * of the one reduction, and the two would disagree the day a status is added: the new member
 * would map correctly forwards and be silently missing from the filter. Filtering the tuple
 * through the existing function cannot drift, and it is total by construction.
 *
 * ⚠️ IT LIVES HERE RATHER THAN BESIDE `officerDutyStateOf`, AND THE REASON IS THE BUDGET.
 * That file is in `constants/` precisely so `pwa/` can import it at runtime; it deliberately
 * imports `ArrangementStatus` as a TYPE ONLY, which erases. Deriving the inverse needs the
 * runtime tuple, which lives in this zod module — and the client does not need the inverse at
 * all, because it sends the state and the server expands it.
 */
export const statusesOfOfficerDutyState = (state: OfficerDutyState): ArrangementStatus[] =>
  ARRANGEMENT_STATUSES.filter((status) => officerDutyStateOf(status) === state);

/**
 * ⚠️ A SLIM PROJECTION OF `ArrangementListItemDto`, NEVER THAT DTO ITSELF.
 *
 * The dashboard's row carries twenty-three fields — policy counts, deployment counts, archive
 * columns, `createdAt`/`updatedAt` — and none of them appears on a phone. Sending them anyway
 * would put roughly 60% more bytes on a Slow 4G connection per row for a screen that renders
 * none of it, and would make every future dashboard field a silent addition to the officer
 * contract the native team builds against.
 *
 * ⚠️ EVERY FIELD HERE IS COPIED, NOT COMPUTED. There is no second derivation of a title, a
 * fill state or a count anywhere in P6d — see `officerOps.service.ts`.
 */
export const opsArrangementListItemSchema = z.object({
  arrangementId: objectIdSchema,
  reference: z.string(),
  /** ⚠️ [C2b §8b] COPIED, like everything here — see `appDutyListItem.paNumber`. */
  paNumber: z.string().nullable(),
  /** The derived title — `arrangementDisplayTitle`, the same string the dashboard list shows. */
  displayTitle: z.string(),
  status: z.enum(ARRANGEMENT_STATUSES),
  /** ADMINISTRATIVE — what the printed order is headed with. */
  paDate: calendarDateSchema,
  /** OPERATIONAL — `null` on a draft that has raised no duty yet. */
  firstDutyDate: calendarDateSchema.nullable(),
  lastDutyDate: calendarDateSchema.nullable(),
  /** The first duty point's first place, plus how many places the order names in total. */
  primaryLocationName: z.string().nullable(),
  placeCount: z.number().int(),
  dutyPointCount: z.number().int(),
  /** Which divisions the order is addressed to — the short codes a chip row renders. */
  divisions: z.array(z.object({ id: objectIdSchema, name: z.string(), shortCode: z.string() })),
  allocatedCount: z.number().int(),
  namedCount: z.number().int(),
  /** FR-5.3.1's four, system-wide: red · amber · green · green-and-locked. */
  fillState: z.enum(FILL_STATES),
  /**
   * ⚠️ [C5 §3] `COMPLETE` + this renders `Closed short`, not `All Filled` — see
   * `arrangementSchema.closedShort`. The PWA's ops row is one of the two screens the client
   * reported: `StatusBadge` and `FillStatePill` sit side by side on it, so it read
   * "Confirmed · Complete" over a duty nobody was coming to.
   *
   * ⚠️ [amended C5b §3] COPIED from the dashboard row, so it carries that row's narrowed meaning:
   * COMPLETE, a share declared short, AND the names short. It read `true` on every fully
   * auto-locked order until C5b (F1 — `$ne: null` of a field most shares lack).
   */
  closedShort: z.boolean(),
  /**
   * ⚠️ [P7A §2] COPIED from the dashboard row's `raisedAtLevel` — the raiser's level by position,
   * `null` meaning no pill. Required-nullable, like its source; nothing here derives it.
   */
  raisedAtLevel: z.enum(RAISER_LEVELS).nullable(),
});

// ── The summary strip — GET /app/operations/summary ──────────────────────────────

export const opsSummaryQuerySchema = z.object({
  dateFrom: calendarDateSchema.optional(),
  dateTo: calendarDateSchema.optional(),
});

/**
 * ⚠️ `value` IS NULLABLE AND THE CARD STILL RENDERS. A tile that timed out must show a dash,
 * never a zero: omitting the card reads as "there is nothing to report", which is the opposite
 * of what a timeout means and is the one misreading that would send a nodal home.
 *
 * ⚠️ AND AN UNAVAILABLE TILE PRODUCES NO CARD AT ALL. `REQUIREMENT_FULFILMENT` is
 * commissionerate-only, so a division nodal's strip is three cards rather than four with a
 * blank — a tile whose question does not apply to your scope is not a missing answer.
 */
export const opsSummaryCardSchema = z.object({
  tileId: z.enum(OPS_SUMMARY_TILE_IDS),
  value: z.number().nullable(),
  unit: z.enum(OPS_SUMMARY_UNITS),
});

export const opsSummarySchema = z.object({
  range: z.object({ from: calendarDateSchema, to: calendarDateSchema }),
  /**
   * Which node the caller is reading from, so the strip can name it — and so a division nodal
   * seeing three cards can tell that from a fourth having failed.
   */
  scope: z.object({ orgUnitType: z.enum(ORG_UNIT_TYPES), orgUnit: orgUnitSummarySchema.nullable() }),
  cards: z.array(opsSummaryCardSchema),
  generatedAt: z.iso.datetime(),
});
